import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

const VEHICLE_ICONS = ['🚗', '🚙', '🛻', '🚚', '🚐', '🏍️', '🚕', '🚓', '🚑', '🚒', '🚌', '🚜', '🚲', '🛵']

export default function ManageVehicles() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [vehicles, setVehicles] = useState([])
  const [profileRateCents, setProfileRateCents] = useState(70)

  // Add/edit modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState(null)

  // Delete/reassign flow state
  const [deleteTarget, setDeleteTarget] = useState(null)   // vehicle being deleted
  const [tripCountForTarget, setTripCountForTarget] = useState(0)
  const [reassignToId, setReassignToId] = useState('')
  const [deleteError, setDeleteError] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const [{ data: veh }, { data: prof }] = await Promise.all([
      supabase
        .from('vehicles')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('profiles')
        .select('mileage_rate_cents')
        .eq('id', user.id)
        .single(),
    ])

    setVehicles(veh || [])
    setProfileRateCents(prof?.mileage_rate_cents ?? 70)
    setLoading(false)
  }

  const openAdd = () => {
    setEditingVehicle(null)
    setModalOpen(true)
  }

  const openEdit = (v) => {
    setEditingVehicle(v)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingVehicle(null)
  }

  const handleSaved = async () => {
    await load()
    closeModal()
  }

  const setAsDefault = async (vehicleId) => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Unset all, then set the chosen one. Two queries for clarity.
    await supabase
      .from('vehicles')
      .update({ is_default: false })
      .eq('user_id', user.id)

    await supabase
      .from('vehicles')
      .update({ is_default: true })
      .eq('id', vehicleId)
      .eq('user_id', user.id)

    await load()
  }

  // Delete flow: first count trips, if any prompt for reassignment
  const beginDelete = async (v) => {
    setDeleteError('')
    const { count } = await supabase
      .from('mileage_trips')
      .select('id', { count: 'exact', head: true })
      .eq('vehicle_id', v.id)

    setDeleteTarget(v)
    setTripCountForTarget(count || 0)
    setReassignToId('')
  }

  const cancelDelete = () => {
    setDeleteTarget(null)
    setTripCountForTarget(0)
    setReassignToId('')
    setDeleteError('')
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setDeleteError('')

    try {
      // If trips exist, reassign first via RPC
      if (tripCountForTarget > 0) {
        if (!reassignToId) {
          setDeleteError(t('vehicles.choose_reassign_target'))
          setDeleting(false)
          return
        }
        const { error: rpcErr } = await supabase.rpc(
          'reassign_trips_between_vehicles',
          { p_from_vehicle_id: deleteTarget.id, p_to_vehicle_id: reassignToId }
        )
        if (rpcErr) throw rpcErr
      }

      // If deleting the default vehicle, promote another to default first
      if (deleteTarget.is_default) {
        const replacement = vehicles.find(v => v.id !== deleteTarget.id)
        if (replacement) {
          await supabase
            .from('vehicles')
            .update({ is_default: true })
            .eq('id', replacement.id)
        }
      }

      const { error: delErr } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', deleteTarget.id)
      if (delErr) throw delErr

      cancelDelete()
      await load()
    } catch (err) {
      console.error('Delete vehicle failed:', err)
      setDeleteError(err.message || t('vehicles.delete_failed'))
    } finally {
      setDeleting(false)
    }
  }

  const fmtRate = (cents) => `${(cents / 100).toFixed(2)}¢/mi`
  const rateDisplay = (v) => v.rate_cents_override != null
    ? fmtRate(v.rate_cents_override)
    : `${fmtRate(profileRateCents)} (default)`

  if (loading) return <div style={styles.loading}>{t('common.loading')}</div>

  return (
    <div>
      {vehicles.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🚗</div>
          <div style={styles.emptyTitle}>{t('vehicles.empty_title')}</div>
          <div style={styles.emptyBody}>{t('vehicles.empty_body')}</div>
        </div>
      ) : (
        <div style={styles.list}>
          {vehicles.map(v => (
            <div key={v.id} style={styles.row}>
              <div style={styles.rowIcon}>{v.icon || '🚗'}</div>
              <div style={styles.rowMain}>
                <div style={styles.rowLabel}>
                  {v.label}
                  {v.is_default && <span style={styles.badge}>{t('vehicles.default')}</span>}
                </div>
                <div style={styles.rowSub}>
                  {[
                    [v.year, v.make, v.model].filter(Boolean).join(' '),
                    rateDisplay(v),
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={styles.rowActions}>
                {!v.is_default && (
                  <button
                    style={styles.btnLink}
                    onClick={() => setAsDefault(v.id)}
                  >
                    {t('vehicles.make_default')}
                  </button>
                )}
                <button style={styles.btnLink} onClick={() => openEdit(v)}>
                  {t('common.edit')}
                </button>
                <button
                  style={styles.btnLinkDanger}
                  onClick={() => beginDelete(v)}
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button style={styles.addBtn} onClick={openAdd}>
        + {t('vehicles.add_vehicle')}
      </button>

      {modalOpen && (
        <VehicleFormModal
          existingVehicle={editingVehicle}
          profileRateCents={profileRateCents}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}

      {deleteTarget && (
        <DeleteVehicleModal
          vehicle={deleteTarget}
          tripCount={tripCountForTarget}
          reassignToId={reassignToId}
          onChangeReassign={setReassignToId}
          otherVehicles={vehicles.filter(v => v.id !== deleteTarget.id)}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          error={deleteError}
          deleting={deleting}
        />
      )}
    </div>
  )
}

// ---------- Form modal (add / edit) ----------
function VehicleFormModal({ existingVehicle, profileRateCents, onClose, onSaved }) {
  const { t } = useTranslation()
  const [label, setLabel] = useState(existingVehicle?.label || '')
  const [icon, setIcon] = useState(existingVehicle?.icon || '🚗')
  const [make, setMake] = useState(existingVehicle?.make || '')
  const [model, setModel] = useState(existingVehicle?.model || '')
  const [yearStr, setYearStr] = useState(existingVehicle?.year != null ? String(existingVehicle.year) : '')
  const [yearStartOdo, setYearStartOdo] = useState(existingVehicle?.year_start_odometer != null ? String(existingVehicle.year_start_odometer) : '')
  const [yearEndOdo, setYearEndOdo] = useState(existingVehicle?.year_end_odometer != null ? String(existingVehicle.year_end_odometer) : '')
  const [useCustomRate, setUseCustomRate] = useState(existingVehicle?.rate_cents_override != null)
  const [customRateDollars, setCustomRateDollars] = useState(
    existingVehicle?.rate_cents_override != null
      ? (existingVehicle.rate_cents_override / 100).toFixed(2)
      : ''
  )
  const [isDefault, setIsDefault] = useState(existingVehicle?.is_default || false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!label.trim()) {
      setError(t('vehicles.label_required'))
      return
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not signed in')

      const parsedYear = yearStr.trim() ? parseInt(yearStr, 10) : null
      const parsedStartOdo = yearStartOdo.trim() ? parseInt(yearStartOdo, 10) : null
      const parsedEndOdo = yearEndOdo.trim() ? parseInt(yearEndOdo, 10) : null

      let rateOverride = null
      if (useCustomRate) {
        const parsedRate = parseFloat(customRateDollars)
        if (!isFinite(parsedRate) || parsedRate < 0) {
          setError(t('vehicles.invalid_rate'))
          setSaving(false)
          return
        }
        rateOverride = Math.round(parsedRate * 100)
      }

      const payload = {
        user_id: user.id,
        label: label.trim(),
        icon,
        make: make.trim() || null,
        model: model.trim() || null,
        year: parsedYear,
        year_start_odometer: parsedStartOdo,
        year_end_odometer: parsedEndOdo,
        rate_cents_override: rateOverride,
        is_default: isDefault,
      }

      if (existingVehicle) {
        const { error: err } = await supabase
          .from('vehicles')
          .update(payload)
          .eq('id', existingVehicle.id)
        if (err) throw err
      } else {
        const { error: err } = await supabase
          .from('vehicles')
          .insert(payload)
        if (err) throw err
      }

      // If set as default, unset default on all OTHER vehicles
      if (isDefault) {
        await supabase
          .from('vehicles')
          .update({ is_default: false })
          .eq('user_id', user.id)
          .neq('id', existingVehicle?.id || '00000000-0000-0000-0000-000000000000')
        // Make sure THIS vehicle is default (in case it was inserted and the above set it false)
        if (existingVehicle) {
          await supabase
            .from('vehicles')
            .update({ is_default: true })
            .eq('id', existingVehicle.id)
        } else {
          // For new insert: re-fetch and mark newest as default
          const { data: latest } = await supabase
            .from('vehicles')
            .select('id')
            .eq('user_id', user.id)
            .eq('label', label.trim())
            .order('created_at', { ascending: false })
            .limit(1)
            .single()
          if (latest) {
            await supabase
              .from('vehicles')
              .update({ is_default: true })
              .eq('id', latest.id)
          }
        }
      }

      onSaved()
    } catch (err) {
      console.error('Save vehicle failed:', err)
      setError(err.message || t('vehicles.save_failed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={m.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={m.modal}>
        <div style={m.header}>
          <div style={m.title}>
            {existingVehicle ? t('vehicles.edit_title') : t('vehicles.add_title')}
          </div>
          <button style={m.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={m.field}>
            <label style={m.label}>{t('vehicles.field_label')} *</label>
            <input
              type="text"
              style={m.input}
              placeholder={t('vehicles.label_placeholder')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
              required
            />
          </div>

          <div style={m.field}>
            <label style={m.label}>{t('vehicles.field_icon')}</label>
            <div style={m.iconPicker}>
              {VEHICLE_ICONS.map(i => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIcon(i)}
                  style={{
                    ...m.iconBtn,
                    ...(icon === i ? m.iconBtnActive : {}),
                  }}
                >{i}</button>
              ))}
            </div>
          </div>

          <div style={m.fieldRow}>
            <div style={m.field}>
              <label style={m.label}>{t('vehicles.field_make')}</label>
              <input
                type="text"
                style={m.input}
                placeholder="Ford"
                value={make}
                onChange={(e) => setMake(e.target.value)}
              />
            </div>
            <div style={m.field}>
              <label style={m.label}>{t('vehicles.field_model')}</label>
              <input
                type="text"
                style={m.input}
                placeholder="F-150"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
            </div>
            <div style={m.field}>
              <label style={m.label}>{t('vehicles.field_year')}</label>
              <input
                type="number"
                style={m.input}
                placeholder="2024"
                min="1900"
                max="2100"
                value={yearStr}
                onChange={(e) => setYearStr(e.target.value)}
              />
            </div>
          </div>

          <div style={m.fieldRow}>
            <div style={m.field}>
              <label style={m.label}>{t('vehicles.field_odo_start')}</label>
              <input
                type="number"
                style={m.input}
                placeholder="0"
                min="0"
                value={yearStartOdo}
                onChange={(e) => setYearStartOdo(e.target.value)}
              />
            </div>
            <div style={m.field}>
              <label style={m.label}>{t('vehicles.field_odo_end')}</label>
              <input
                type="number"
                style={m.input}
                placeholder="0"
                min="0"
                value={yearEndOdo}
                onChange={(e) => setYearEndOdo(e.target.value)}
              />
            </div>
          </div>
          <div style={m.helperText}>{t('vehicles.odo_help')}</div>

          <div style={m.field}>
            <label style={m.checkboxRow}>
              <input
                type="checkbox"
                checked={useCustomRate}
                onChange={(e) => {
                  const checked = e.target.checked
                  setUseCustomRate(checked)
                  // Auto-fill with profile default if turning on and field is empty
                  if (checked && !customRateDollars.trim()) {
                    setCustomRateDollars((profileRateCents / 100).toFixed(2))
                  }
                }}
                style={m.checkbox}
              />
              <span style={m.checkboxLabel}>{t('vehicles.use_custom_rate')}</span>
            </label>
            {useCustomRate && (
              <div style={{ marginTop: '8px' }}>
                <label style={m.label}>{t('vehicles.custom_rate_label')}</label>
                <div style={m.rateRow}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    style={{ ...m.input, width: '120px' }}
                    placeholder="0.70"
                    value={customRateDollars}
                    onChange={(e) => setCustomRateDollars(e.target.value)}
                  />
                  <span style={m.rateUnit}>¢/mile</span>
                </div>
                <div style={m.helperText}>
                  {t('vehicles.default_rate_is', { rate: (profileRateCents / 100).toFixed(2) })}
                </div>
              </div>
            )}
          </div>

          <label style={m.checkboxRow}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              style={m.checkbox}
            />
            <span style={m.checkboxLabel}>{t('vehicles.set_default')}</span>
          </label>

          {error && <div style={m.error}>{error}</div>}

          <div style={m.actions}>
            <button type="button" style={m.btnSecondary} onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              style={{ ...m.btnPrimary, opacity: saving ? 0.5 : 1 }}
              disabled={saving}
            >
              {saving ? t('common.saving') : (existingVehicle ? t('common.save_changes') : t('vehicles.add_vehicle'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------- Delete modal with reassignment ----------
function DeleteVehicleModal({ vehicle, tripCount, reassignToId, onChangeReassign, otherVehicles, onConfirm, onCancel, error, deleting }) {
  const { t } = useTranslation()
  const hasTrips = tripCount > 0
  const canConfirm = !hasTrips || !!reassignToId

  return (
    <div style={m.overlay} onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div style={{ ...m.modal, maxWidth: '460px' }}>
        <div style={m.header}>
          <div style={m.title}>{t('vehicles.delete_title')}</div>
          <button style={m.closeBtn} onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div style={m.deleteBody}>
          <div style={m.deleteIcon}>{vehicle.icon || '🚗'}</div>
          <div style={m.deleteVehicleLabel}>{vehicle.label}</div>

          {hasTrips ? (
            <>
              <div style={m.deletePrompt}>
                {t('vehicles.has_trips_prompt', { count: tripCount })}
              </div>
              <div style={m.field}>
                <label style={m.label}>{t('vehicles.reassign_to')}</label>
                <select
                  style={m.input}
                  value={reassignToId}
                  onChange={(e) => onChangeReassign(e.target.value)}
                >
                  <option value="">{t('vehicles.pick_vehicle')}</option>
                  {otherVehicles.map(ov => (
                    <option key={ov.id} value={ov.id}>
                      {ov.icon || '🚗'} {ov.label}
                    </option>
                  ))}
                </select>
              </div>
              {otherVehicles.length === 0 && (
                <div style={m.warning}>{t('vehicles.no_other_vehicles')}</div>
              )}
            </>
          ) : (
            <div style={m.deletePrompt}>{t('vehicles.delete_confirm')}</div>
          )}
        </div>

        {error && <div style={m.error}>{error}</div>}

        <div style={m.actions}>
          <button type="button" style={m.btnSecondary} onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            style={{
              ...m.btnDanger,
              opacity: (!canConfirm || deleting || (otherVehicles.length === 0 && hasTrips)) ? 0.4 : 1,
              cursor: (!canConfirm || deleting) ? 'not-allowed' : 'pointer',
            }}
            onClick={onConfirm}
            disabled={!canConfirm || deleting || (hasTrips && otherVehicles.length === 0)}
          >
            {deleting ? t('common.deleting') : t('vehicles.confirm_delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------- Styles ----------
const styles = {
  loading: { padding: '20px', color: '#9CA3AF', fontSize: '14px', textAlign: 'center' },
  empty: { padding: '28px 16px', textAlign: 'center' },
  emptyIcon: { fontSize: '36px', marginBottom: '8px' },
  emptyTitle: { fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: plum, marginBottom: '4px' },
  emptyBody: { fontSize: '13px', color: '#6B7280', maxWidth: '320px', margin: '0 auto', lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 14px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
  },
  rowIcon: { fontSize: '28px', width: '36px', textAlign: 'center' },
  rowMain: { flex: 1, minWidth: 0 },
  rowLabel: {
    fontSize: '14px', fontWeight: 700, color: plum,
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  rowSub: {
    fontSize: '12px', color: '#6B7280', fontWeight: 500,
    marginTop: '2px',
  },
  badge: {
    fontSize: '10px', fontWeight: 700, color: teal,
    background: '#E6FFFA', padding: '2px 8px', borderRadius: '8px',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  rowActions: { display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' },
  btnLink: {
    background: 'transparent', border: 'none', color: teal,
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    padding: '4px 8px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnLinkDanger: {
    background: 'transparent', border: 'none', color: '#DC2626',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    padding: '4px 8px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  addBtn: {
    width: '100%', padding: '12px',
    background: teal, color: '#042f2e', border: 'none',
    borderRadius: '100px', fontSize: '13px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
}

const m = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 3000,
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '20px', padding: '24px',
    width: '100%', maxWidth: '520px', maxHeight: '92vh',
    overflowY: 'auto',
    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum,
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '20px',
  },
  title: {
    fontFamily: "'Fraunces', serif", fontSize: '22px',
    fontWeight: 800, color: plum, letterSpacing: '-0.3px',
  },
  closeBtn: {
    background: '#F3F4F6', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px',
    fontSize: '14px', color: '#6B7280', cursor: 'pointer', fontWeight: 700,
  },
  field: { marginBottom: '14px' },
  fieldRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px' },
  label: {
    display: 'block', fontSize: '12px', fontWeight: 700,
    color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  helperText: { fontSize: '11px', color: '#9CA3AF', fontWeight: 500, marginTop: '-6px', marginBottom: '14px' },
  iconPicker: {
    display: 'flex', flexWrap: 'wrap', gap: '6px',
    padding: '10px', background: '#F9FAFB', borderRadius: '10px',
    border: '1px solid #E5E7EB',
  },
  iconBtn: {
    width: '38px', height: '38px',
    background: '#fff', border: '1.5px solid #E5E7EB',
    borderRadius: '8px', fontSize: '20px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  iconBtnActive: {
    background: '#E6FFFA',
    border: `2px solid ${teal}`,
  },
  checkboxRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 14px',
    background: '#F9FAFB', border: '1px solid #E5E7EB',
    borderRadius: '10px', cursor: 'pointer',
    marginBottom: '8px',
  },
  checkbox: { accentColor: teal, width: '18px', height: '18px', cursor: 'pointer' },
  checkboxLabel: { fontSize: '14px', fontWeight: 600, color: plum },
  rateRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  rateUnit: { fontSize: '13px', color: '#6B7280', fontWeight: 500 },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '10px 14px', borderRadius: '8px',
    fontSize: '13px', marginTop: '12px', marginBottom: '12px',
    fontWeight: 500,
  },
  warning: {
    background: '#FEF3C7', color: '#92400E',
    padding: '10px 14px', borderRadius: '8px',
    fontSize: '13px', fontWeight: 500,
    marginTop: '8px',
  },
  actions: { display: 'flex', gap: '10px', marginTop: '6px' },
  btnPrimary: {
    flex: 2, padding: '13px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnSecondary: {
    flex: 1, padding: '13px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    flex: 2, padding: '13px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: 700,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.3)',
  },
  deleteBody: { padding: '12px 0', textAlign: 'center' },
  deleteIcon: { fontSize: '48px', marginBottom: '8px' },
  deleteVehicleLabel: {
    fontFamily: "'Fraunces', serif", fontSize: '20px',
    fontWeight: 800, color: plum, marginBottom: '12px',
  },
  deletePrompt: {
    fontSize: '14px', color: '#374151', lineHeight: 1.5,
    marginBottom: '18px',
  },
}
